import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useAtom } from 'jotai';
import { useFlowConfigs } from '@src/hooks/flow/useFlowConfigs';
import { Input, Space, Tag, Divider } from 'antd';
import { selectedFlowsAtom } from '../../atoms';
import { CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import {
  StyledBody,
  FlowBox,
  FlowContent,
  FlowName,
  FlowLabels,
  FlowLabel,
  StyledCheckableTag,
  SectionHeader,
  EmptyState,
  FlowMainPanel,
  DrawerCardWrapper,
} from './styled';
const DrawerCard = ({ isDrawerOpen }: { isDrawerOpen: boolean }) => {
  const [selectedFlowIds, setSelectedFlowIds] = useAtom(selectedFlowsAtom);
  const flowConfigs = useFlowConfigs();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const searchInputRef = useRef<any>(null);

  // Focus search input when drawer opens
  useEffect(() => {
    if (isDrawerOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isDrawerOpen]);

  // Get all unique labels from flows
  const allLabels = useMemo(() => {
    if (!flowConfigs) return [];

    const labelSet = new Set<string>();
    Object.values(flowConfigs).forEach((flow) => {
      if (flow.labels && Array.isArray(flow.labels)) {
        flow.labels.forEach((label) => {
          if (label) labelSet.add(label);
        });
      }
    });

    return Array.from(labelSet).sort();
  }, [flowConfigs]);

  // Apply search filter
  const flowsBySearch = useMemo(() => {
    if (!flowConfigs || !searchTerm) return Object.values(flowConfigs || {});

    const lowerSearch = searchTerm.toLowerCase();
    return Object.values(flowConfigs).filter(
      (flow) =>
        flow.name?.toLowerCase().includes(lowerSearch) ||
        flow.identifier?.toLowerCase().includes(lowerSearch),
    );
  }, [flowConfigs, searchTerm]);

  // Apply label filter and sort alphabetically
  const filteredFlows = useMemo(() => {
    const flows =
      selectedLabels.length === 0
        ? flowsBySearch
        : flowsBySearch.filter((flow) => {
            if (!flow.labels || !Array.isArray(flow.labels)) return false;
            // Show flow if it has at least one of the selected labels
            return flow.labels.some((label) => selectedLabels.includes(label));
          });

    return flows.sort((a, b) => a.name.localeCompare(b.name));
  }, [flowsBySearch, selectedLabels]);

  const toggleSelect = (id: number) => {
    const idStr = id.toString();
    const updated = selectedFlowIds.includes(idStr)
      ? selectedFlowIds.filter((s) => s !== idStr)
      : [...selectedFlowIds, idStr];

    setSelectedFlowIds(updated);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const toggleLabel = (label: string) => {
    setSelectedLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  if (!flowConfigs || Object.keys(flowConfigs).length === 0) {
    return (
      <div>
        <div>No flows available.</div>
      </div>
    );
  }

  return (
    <DrawerCardWrapper>
      <Space direction="vertical" size="middle" style={{ flexShrink: 0 }}>
        <Input
          ref={searchInputRef}
          placeholder="Search flows..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={
            searchTerm ? (
              <CloseCircleOutlined
                onClick={handleClearSearch}
                style={{ cursor: 'pointer', color: '#999' }}
              />
            ) : null
          }
          allowClear={false}
        />
        {allLabels.length > 0 && (
          <div>
            <SectionHeader>Labels:</SectionHeader>
            {allLabels.map((label) => (
              <StyledCheckableTag
                key={label}
                checked={selectedLabels.includes(label)}
                $checked={selectedLabels.includes(label)}
                onChange={() => toggleLabel(label)}
              >
                {label}
              </StyledCheckableTag>
            ))}
          </div>
        )}
      </Space>

      <Divider size="small" style={{ flexShrink: 0 }} />

      {filteredFlows.length === 0 ? (
        <EmptyState>No flows found matching your criteria.</EmptyState>
      ) : (
        <FlowMainPanel>
          {filteredFlows.map((item) => {
            const isSelected = selectedFlowIds.includes(item.id.toString());
            const hasLabels = item.labels && Array.isArray(item.labels) && item.labels.length > 0;
            return (
              <FlowBox onClick={() => toggleSelect(item.id)} $isSelected={isSelected} key={item.id}>
                <StyledBody>
                  <FlowContent>
                    <FlowName>{item.name}</FlowName>
                    {hasLabels && (
                      <FlowLabels>
                        {item.labels.map((label) => (
                          <FlowLabel key={label}>
                            <Tag color="green">{label}</Tag>
                          </FlowLabel>
                        ))}
                      </FlowLabels>
                    )}
                  </FlowContent>
                </StyledBody>
              </FlowBox>
            );
          })}
        </FlowMainPanel>
      )}
    </DrawerCardWrapper>
  );
};

export default DrawerCard;
